/**
 * SYSTEM SETTINGS MODULE
 * Where the Retail Product Finder gets its store list, and how far back it
 * looks. Account-scoped, read/written over RLS like the rest of this module.
 *
 * Two sources:
 *   'depletions'   — derive stores from depletion rows the tenant already syncs
 *   'google_sheet' — a dedicated sheet of addresses (the GoogleSheetsPanel path)
 *
 * `recency_months` applies to BOTH sources and means the same thing in each —
 * "hide a store we haven't seen for this long" — only the evidence differs:
 *   depletions   → seen = had sales in the window (`retail_locations.last_sold_on`)
 *   google_sheet → seen = appeared in the file    (`retail_locations.last_seen_at`)
 * NULL is "no cutoff", i.e. never hide a store automatically.
 *
 * Stored in months because depletions are keyed by (year, month) — the UI offers
 * the familiar 30/60/90-day windows, which are exactly 1/2/3 months.
 *
 * ⚠️ For the sheet source this is a GRACE PERIOD, not a filter. Before migration
 * 0082 a store that fell out of the sheet was deactivated on the very next sync,
 * so the effective window was zero and one partial file could empty a customer's
 * public map within the hour.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";

// Only these ever reach the table; account_id is injected, and the DB-owned
// columns (last_synced_at, last_sync_report) are never accepted from a caller.
const SAVEABLE = ["source_kind", "recency_months"];

const SOURCE_KINDS = new Set(["depletions", "google_sheet"]);

/** Windows offered in the UI. Value is months; depletions have no finer grain. */
export const RECENCY_OPTIONS = [
  { value: 1,    label: "Last 30 days" },
  { value: 2,    label: "Last 60 days" },
  { value: 3,    label: "Last 90 days" },
  { value: 6,    label: "Last 6 months" },
  { value: 12,   label: "Last 12 months" },
  { value: null, label: "No cutoff — keep every store" },
];

let _acct = null;
async function acctId() {
  if (_acct) return _acct;
  const a = await getMyPrimaryAccount();
  if (!a?.accountId) throw new Error("No workspace found for your account.");
  _acct = a.accountId;
  return _acct;
}

/** Reset the cached account (call on sign-out / workspace switch). */
export function resetRetailSourceAccount() { _acct = null; }

/** The tenant's retail source config, or null when never configured. */
export async function getRetailSource() {
  const account = await acctId();
  const { data, error } = await supabase
    .from("retail_data_source")
    .select("account_id, source_kind, recency_months, last_synced_at, last_sync_report")
    .eq("account_id", account)
    .maybeSingle();
  if (error) {
    console.error("[retailSourceService.getRetailSource]", error);
    throw new Error("Could not load the retail location source.");
  }
  return data;
}

/** Upsert the config (whitelisted columns only; account_id injected). */
export async function saveRetailSource(patch = {}) {
  const account = await acctId();
  const row = { account_id: account };

  for (const k of SAVEABLE) {
    if (patch[k] === undefined) continue;
    if (k === "source_kind") {
      if (patch[k] !== null && !SOURCE_KINDS.has(patch[k])) {
        throw new Error("Unknown source.");
      }
      row[k] = patch[k];
    }
    if (k === "recency_months") {
      const v = patch[k];
      if (v === null || v === "") { row[k] = null; continue; }
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 120) throw new Error("Invalid time window.");
      row[k] = n;
    }
  }

  const { data, error } = await supabase
    .from("retail_data_source")
    .upsert(row, { onConflict: "account_id" })
    .select("account_id, source_kind, recency_months, last_synced_at, last_sync_report")
    .single();
  if (error) {
    console.error("[retailSourceService.saveRetailSource]", error);
    throw new Error("Could not save the retail location source.");
  }
  return data;
}

/**
 * Derive the store list from depletion data (backlog items 31 + 37).
 *
 * Calls `resolve_depletion_outlets` — a security-definer RPC, invoked BY NAME.
 * That is the documented cross-module contract (MODULES.md rule 4, and the
 * pattern incentive-tracker/RESOLVER_DESIGN.md argues for): System Settings
 * never reads `depletions` or `retail_locations` directly, so no import and no
 * table read crosses a module boundary.
 *
 * Safe to press repeatedly — the resolver is idempotent and never deletes.
 * Returns { outlets_created, rows_linked, outlets_refreshed, outlets_retired,
 *           rows_unkeyed, recency_months, cutoff }.
 */
export async function rebuildOutletsFromDepletions() {
  const account = await acctId();
  const { data, error } = await supabase.rpc("resolve_depletion_outlets", {
    p_account_id: account,
  });
  if (error) {
    console.error("[retailSourceService.rebuildOutletsFromDepletions]", error);
    throw new Error("Could not rebuild the store list from depletions.");
  }
  return data ?? {};
}
